import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import NextLink from 'next/link';
import { DEFAULT_PATH } from 'config';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  alpha,
  keyframes,
  useTheme
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AnimateButton from 'components/@extended/AnimateButton';
import IconButton from 'components/@extended/IconButton';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import useBlockUi from 'hooks/useBlockUi';
import useLocale from 'hooks/useLocale';
import { signInWithCredentials } from 'lib/auth/credentialsSignIn';

interface AuthLoginProps {
  csrfToken: string | null;
  onUnverified: (email: string, password: string) => void;
}

interface LoginFormValues {
  email: string;
  password: string;
  submit: string | null;
}

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const AuthLogin = ({ csrfToken, onUnverified }: AuthLoginProps) => {
  const [capsWarning, setCapsWarning] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const block = useBlockUi();
  const { data: session } = useSession();
  const theme = useTheme();
  const { t } = useLocale();

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string().max(255).email(t('auth.validationUsername')).required(t('auth.validationUsernameRequired')),
    password: Yup.string().max(255).required(t('auth.validationPasswordRequired'))
  });

  const initialValues: LoginFormValues = {
    email: '',
    password: '',
    submit: null
  };

  useEffect(() => {
    block.handleLoad(false);
    localStorage.removeItem('loginCompany');
  }, [block]);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleMouseDownPassword = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsWarning(event.getModifierState('CapsLock'));
  }, []);

  const handleSubmit = useCallback(
    async (values: LoginFormValues, { setErrors, setSubmitting }: FormikHelpers<LoginFormValues>) => {
      try {
        localStorage.clear();

        const res = await signInWithCredentials(values.email, values.password);

        if (!res.ok) {
          setErrors({ submit: res.error });
          if (res.isUnverified) {
            onUnverified(values.email, values.password);
          }
          return;
        }

        // โหลดหน้าใหม่เพื่อให้ cookie session ถูกอ่านและ GuestGuard / layout ทำงานสม่ำเสมอ
        if (typeof window !== 'undefined') {
          window.location.href = DEFAULT_PATH;
        }
      } catch {
        setErrors({ submit: t('auth.loginError') });
      } finally {
        setSubmitting(false);
      }
    },
    [onUnverified]
  );

  return (
    <Formik initialValues={initialValues} validationSchema={loginValidationSchema} onSubmit={handleSubmit}>
      {({ errors, handleBlur, handleChange, handleSubmit: formikSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={formikSubmit}>
          <input name="csrfToken" type="hidden" defaultValue={csrfToken ?? ''} />

          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h3">{t('auth.signIn')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t('auth.enterInfoToLogin')}
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            <Stack spacing={1}>
              <InputLabel htmlFor="email-login">{t('auth.userEmail')}</InputLabel>
              <OutlinedInput
                id="email-login"
                type="email"
                value={values.email}
                name="email"
                autoComplete="username"
                inputProps={{ 'aria-describedby': 'helper-text-email-login' }}
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder={t('auth.enterUsername')}
                fullWidth
                error={Boolean(touched.email && errors.email)}
                sx={{
                  transition: 'box-shadow 0.25s ease',
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
                  }
                }}
              />
              {touched.email && errors.email && (
                <FormHelperText error id="helper-text-email-login">
                  {errors.email}
                </FormHelperText>
              )}
            </Stack>

            <Stack spacing={1}>
              <InputLabel htmlFor="password-login">{t('auth.password')}</InputLabel>
              <OutlinedInput
                fullWidth
                color={capsWarning ? 'warning' : 'primary'}
                error={Boolean(touched.password && errors.password)}
                id="password-login"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                name="password"
                autoComplete="current-password"
                inputProps={{ 'aria-describedby': 'helper-text-password-login' }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  setCapsWarning(false);
                  handleBlur(event);
                }}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      color="secondary"
                    >
                      {showPassword ? <EyeOutlined rev={undefined} /> : <EyeInvisibleOutlined rev={undefined} />}
                    </IconButton>
                  </InputAdornment>
                }
                placeholder={t('auth.enterPassword')}
                sx={{
                  transition: 'box-shadow 0.25s ease',
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`
                  }
                }}
              />
              {capsWarning && (
                <Typography variant="caption" sx={{ color: 'warning.main' }} id="warning-helper-text-password-login" role="status">
                  {t('auth.capsLockOn')}
                </Typography>
              )}
              {touched.password && errors.password && (
                <FormHelperText error id="helper-text-password-login">
                  {errors.password}
                </FormHelperText>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <NextLink
                href={session ? '/auth/forgot-password' : '/forgot-password'}
                style={{ textDecoration: 'none' }}
              >
                <Typography variant="body2" color="primary" sx={{ '&:hover': { color: 'primary.dark' } }}>
                  {t('auth.forgotPassword')}
                </Typography>
              </NextLink>
            </Box>

            {errors.submit && (
              <FormHelperText error role="alert" aria-live="polite">
                {errors.submit}
              </FormHelperText>
            )}

            <AnimateButton>
              <Button
                disableElevation
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 600,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                  '&:hover': { transform: 'translateY(-1px)' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent 0%, ${alpha('#fff', 0.15)} 50%, transparent 100%)`,
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 3s ease-in-out infinite`,
                    pointerEvents: 'none'
                  }
                }}
              >
                {isSubmitting ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={20} color="inherit" />
                    <span>{t('auth.signingIn')}</span>
                  </Stack>
                ) : (
                  t('auth.signIn')
                )}
              </Button>
            </AnimateButton>
          </Stack>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t('common.or')}
            </Typography>
          </Divider>

          <Stack direction="row" justifyContent="center" spacing={0.5}>
            <Typography variant="body1" color="text.secondary">
              {t('auth.noAccount')}
            </Typography>
            <NextLink href="/register" style={{ textDecoration: 'none' }}>
              <Typography variant="body1" color="primary" fontWeight={600} sx={{ '&:hover': { color: 'primary.dark' } }}>
                {t('auth.signUp')}
              </Typography>
            </NextLink>
          </Stack>
        </form>
      )}
    </Formik>
  );
};

export default AuthLogin;
